import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Mail, MapPin } from 'lucide-react';
import Logo from '../components/Logo.png';
import '../styles/Navbar.css';

const Navbar = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        const handleScroll = () => setIsScrolled(window.scrollY > 50);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        if (!isOpen) return;
        const onKey = (e) => { if (e.key === 'Escape') setIsOpen(false); };
        window.addEventListener('keydown', onKey);
        document.body.style.overflow = 'hidden';
        return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
    }, [isOpen]);

    const menuItems = [
        { id: 'inicio', label: 'Início' },
        { id: 'produtos', label: 'Produtos' },
        { id: 'cursos', label: 'Cursos' },
        { id: 'certificados', label: 'Acreditações' },
        { id: 'servicos', label: 'Serviços' },
        { id: 'sobre', label: 'Sobre' },
    ];

    const handleNavClick = (pageId) => {
        const path = pageId === 'inicio' ? '/' : `/${pageId}`;
        navigate(path);
        setIsOpen(false);
    };

    const isActive = (pageId) => {
        const expectedPath = pageId === 'inicio' ? '/' : `/${pageId}`;
        return location.pathname === expectedPath;
    };

    return (
        <>
            {/* Top Bar */}
            <div className="top-bar" aria-label="Informações de contacto">
                <div className="top-bar-content">
                    <div className="contact-info">
                        <div className="contact-item">
                            <Phone size={16} className="contact-icon" aria-hidden="true" />
                            <span>
                                <a href="tel:+258877041104" aria-label="Telefone: +258 87 704 1104">
                                    +258 87 704 1104
                                </a>
                            </span>
                        </div>
                        <div className="contact-item">
                            <Mail size={16} className="contact-icon" aria-hidden="true" />
                            <span>
                                <a href="mailto:adm@firesafety.co.mz" aria-label="Email: adm@firesafety.co.mz">
                                    adm@firesafety.co.mz
                                </a>
                            </span>
                        </div>
                        <div className="contact-item">
                            <MapPin size={16} className="contact-icon" aria-hidden="true" />
                            <span>AV. Olof Palme nº245, 1º andar, Maputo - Moçambique</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Navbar */}
            <nav className={`navbar ${isScrolled ? 'scrolled' : ''}`} aria-label="Navegação principal">
                <div className="nav-container">
                    {/* Logo */}
                    <div className="nav-brand">
                        <div className="brand-logo">
                            <img
                                src={Logo}
                                alt="Fire Safety Moçambique Logo"
                                className="logo-image"
                                loading="lazy"
                                onError={(e) => {
                                    e.target.src = 'https://via.placeholder.com/150x50?text=Logo';
                                }}
                            />
                        </div>
                    </div>

                    {/* Desktop Menu */}
                    <div className="nav-menu desktop-menu">
                        {menuItems.map(({ id, label }) => (
                            <button
                                key={id}
                                className={`nav-link ${isActive(id) ? 'active' : ''}`}
                                onClick={() => handleNavClick(id)}
                                aria-current={isActive(id) ? 'page' : undefined}
                            >
                                {label}
                            </button>
                        ))}
                        <button
                            className="cta-button"
                            onClick={() => navigate('/contatos')}
                            aria-label="Contactar agora"
                        >
                            Contactar Agora
                        </button>
                    </div>

                    {/* Mobile Menu Toggle */}
                    <button
                        className="mobile-menu-btn"
                        onClick={() => setIsOpen(!isOpen)}
                        aria-label={isOpen ? 'Fechar menu' : 'Abrir menu'}
                        aria-expanded={isOpen}
                    >
                        {isOpen ? (
                            <X size={24} className="menu-icon" aria-hidden="true" />
                        ) : (
                            <Menu size={24} className="menu-icon" aria-hidden="true" />
                        )}
                    </button>
                </div>

                {/* Mobile Menu */}
                <div className={`mobile-menu ${isOpen ? 'open' : ''}`}>
                    <div className="mobile-menu-content">
                        {menuItems.map(({ id, label }, index) => (
                            <button
                                key={id}
                                className={`mobile-nav-link ${isActive(id) ? 'active' : ''}`}
                                onClick={() => handleNavClick(id)}
                                style={{ transitionDelay: `${index * 0.1}s` }}
                                aria-current={isActive(id) ? 'page' : undefined}
                            >
                                {label}
                            </button>
                        ))}
                        <button
                            className="mobile-cta-button"
                            onClick={() => {
                                navigate('/contatos');
                                setIsOpen(false);
                            }}
                            style={{ transitionDelay: `${menuItems.length * 0.1}s` }}
                            aria-label="Contactar agora"
                        >
                            Contactar Agora
                        </button>
                    </div>
                </div>

                {/* Overlay */}
                {isOpen && <div className="mobile-overlay" onClick={() => setIsOpen(false)} aria-hidden="true" />}
            </nav>
        </>
    );
};

export default Navbar;